import {
  ColDef,
  ColGroupDef,
  FilterChangedEvent,
  GridApi,
  IRowNode,
  PostSortRowsParams,
  RowClassParams,
  RowDataUpdatedEvent,
  SelectionChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { saveAs } from 'file-saver-es';
import { flatMap, flow, groupBy, uniqBy } from 'lodash-es';
import { derivedAsync } from 'ngxtension/derived-async';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { Observable, finalize } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { SubSink } from 'subsink';

import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import {
  AuthFacade,
  DomainDataFacade,
  ParsedWebsocketMessage,
  PermissionUtils,
  ResourcesService,
  RouteFacade,
  ToastService,
  UserSettingsFacade,
} from '@mitsubishi/core';
import { CHILLER_FREE_ST_CLASS, CHILLER_FREE_ST_CLASS_NAME } from '@mitsubishi/project/chiller-free-st';
import {
  AccessoryType,
  ProjectFacade,
  StudyFacade,
  StudyPerformanceFiltersStore,
  StudyService,
  StudyUtils,
  TemporaryStudyItemsService,
  unitUtilsFactory,
} from '@mitsubishi/project/data-access';
import {
  AccessoriesErrorConfigCodes,
  Class,
  OutputConfiguration,
  OutputItemRequest,
  OutputsService,
  PRICE_TYPE_LABEL,
  PerformanceEnvelop,
  PerformanceFilter,
  PerformanceFiltersPayload,
  PerformanceValue,
  PriceTypeEnum,
  Study,
  StudySubcategory,
  StudyUnit,
  StudyUnitAccessoryWithPrice,
  StudyUnitPerformanceRow,
  StudyUnitTotalPrices,
  UnitPerformanceAccessoryAnswers,
} from '@mitsubishi/shared/data-access';
import {
  RequestTraceCommonMessages,
  RequestTraceData,
  RequestTracingComponent,
} from '@mitsubishi/shared/request-tracing';
import {
  ConfirmationDialogData,
  DialogImage,
  DialogService,
  ImageDialogComponent,
  ImageDialogData,
} from '@mitsubishi/ui/dialog';
import { PriceStore } from '@mitsubishi/ui/pricing';
import {
  ApmGridColumnType,
  TABLE_DEFAULT_DELETE_COL_DEF,
  TableActionClickEvent,
  TableClickableCellComponent,
  TableComponent,
  TableGetCustomIconColumnDef,
  TableIconActions,
  TableImageCellComponent,
  TablePriceHeaderComponent,
  TableResourceHeaderComponent,
  TableResourceHeaderComponentParams,
} from '@mitsubishi/ui/table';
import { GridUtils, MiscUtils, generatePerformanceKey } from '@mitsubishi/utils';

import { Nullable } from '@angular-ru/cdk/typings';
import { isNil } from '@angular-ru/cdk/utils';

import { ClassSelectionComponent } from '../class-selection/class-selection.component';
import {
  ConfigurationAlertsDialogComponent,
  ConfigurationAlertsDialogComponentProps,
} from '../configuration-alerts-dialog/configuration-alerts-dialog.component';
import {
  PerformanceAccessoriesAssignmentDialogAction,
  PerformanceAccessoriesAssignmentDialogComponent,
  PerformanceAccessoriesAssignmentDialogProps,
  PerformanceAccessoriesAssignmentDialogResult,
} from '../performance-accessories-assignment-dialog/performance-accessories-assignment-dialog.component';
import { ApmProjectCurrencyPipe } from '../pipes/apm-project-currency.pipe';
import {
  UnitAccessoriesListDialogData,
  UnitAccessoryListDialogComponent,
} from '../unit-accessory-list-dialog/unit-accessory-list-dialog.component';
import { StudyPerformanceHeaderComponent } from './components/study-performance-header/study-performance-header.component';
import { TablePerformanceAlertsCellComponent } from './components/table-performance-alerts-cell/table-performance-alerts-cell.component';
import { StudyWorkingConditionColorsMap, StudyWorkingConditionStore } from './study-working-condition.store';

const tableIconsActions = {
  TOGGLE_CREATE_SINGLE_UNIT: 'TOGGLE_CREATE_SINGLE_UNIT',
  TOGGLE_GOOD_FOR_SELECTION: 'TOGGLE_GOOD_FOR_SELECTION',
  SHOW_WORKING_CONDITIONS: 'SHOW_WORKING_CONDITIONS',
  GOTO_UNIT_CONFIGURATION: 'GOTO_UNIT_CONFIGURATION',
  DOWNLOAD_PERFORMANCE_REQUEST_LOG: 'DOWNLOAD_PERFORMANCE_REQUEST_LOG',
  ASSIGN_PERFORMANCE_ACCESSORIES: 'ASSIGN_PERFORMANCE_ACCESSORIES',
  SHOW_ACCESSORIES_WITH_PRICE: 'SHOW_ACCESSORIES_WITH_PRICE',
  SHOW_EXTERNAL_UNIT_ACCESSORIES_WITH_PRICE: 'SHOW_EXTERNAL_UNIT_ACCESSORIES_WITH_PRICE',
  SHOW_ENVELOPES: 'SHOW_ENVELOPES',
};

@Component({
  selector: 'apm-study-models',
  templateUrl: './study-models.component.html',
  styleUrls: ['./study-models.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PriceStore, ApmProjectCurrencyPipe],
  standalone: false,
})
export class StudyModelsComponent extends RequestTracingComponent implements OnInit, OnDestroy {
  readonly tableRef = viewChild<TableComponent | undefined>('table');

  readonly study = input.required<Study>();
  readonly gridId = input('study-models-grid');
  readonly subCategory = input<StudySubcategory | undefined>();
  readonly showActions = input(true);
  readonly isInterimStudy = input(false);
  readonly overrideCurrencyCode = input<string | undefined>();

  performanceAccessoriesAssigned = output<void>();
  performanceColumnsCreated = output<PerformanceValue[]>();

  router = inject(Router);
  routeFacade = inject(RouteFacade);
  activatedRoute = inject(ActivatedRoute);
  dialogService = inject(DialogService);
  toastService = inject(ToastService);
  resourcesService = inject(ResourcesService);
  projectCurrencyPipe = inject(ApmProjectCurrencyPipe);
  studyFacade = inject(StudyFacade);
  sharedDataFacade = inject(DomainDataFacade);
  projectFacade = inject(ProjectFacade);
  studyService = inject(StudyService);
  outputService = inject(OutputsService);
  userSettingsFacade = inject(UserSettingsFacade);

  #authFacade = inject(AuthFacade);
  #performanceFiltersStore = inject(StudyPerformanceFiltersStore);
  #studyWorkingConditionStore = inject(StudyWorkingConditionStore);
  #tablePriceStore = inject(PriceStore);
  #temporaryStudyItemsService = inject(TemporaryStudyItemsService);

  #unitUtils = unitUtilsFactory();

  hideWorkingCondition$ = this.userSettingsFacade.hideWorkingCondition$;
  isFromStudy = this.routeFacade.isStudyModuleSignal;

  defaultColumnDefs: (ColDef | ColGroupDef)[] = [];
  columnDefs: (ColDef | ColGroupDef)[] = [];
  imageDialogComponentRef: MatDialogRef<ImageDialogComponent> | undefined;

  selectedRows: StudyUnitPerformanceRow[] = [];
  unitWithAccessories: StudyUnitPerformanceRow[] = [];

  canDebug = this.#authFacade.canDebug;
  hasDatasheetMoreDetails = PermissionUtils.hasDatasheetMoreDetails();
  hasChillersFreeStManagePermission = PermissionUtils.hasChillersFreeStManagePermission();

  tempStudyItems: string[] = [];
  readonly columnsReady = signal(false);

  showPrices = true;
  readonly #selectedPrice = this.#tablePriceStore.activePriceType;

  PRICE_TYPE_LABEL = PRICE_TYPE_LABEL;

  priceColumnsToRefresh = ['unitTotalPrices', 'externalUnitTotalPrices'];
  defaultModelColumnTypes = [
    ApmGridColumnType.WHITE_BACKGROUND,
    ApmGridColumnType.PRE_WRAP,
    ApmGridColumnType.CENTERED_CONTENT,
  ];
  defaultExternalModelColumnTypes = [ApmGridColumnType.PRE_WRAP];
  rowSpanClassRule = {
    'ag-grid-cell-span': 'data.rowSpan > 1',
  };
  priceClassRules = {
    'ag-grid-sell-in-price-cell': () => {
      return this.#selectedPrice() === PriceTypeEnum.sellIn;
    },
    'ag-grid-base-price-cell': () => {
      return this.#selectedPrice() === PriceTypeEnum.basePrice;
    },
  };

  readonly subCategoryId = computed(() => {
    return this.subCategory()?.uuid;
  });

  readonly uniqueGridId = computed(() => {
    let id = this.gridId();
    const subCategory = this.subCategory();
    if (subCategory) {
      id = `${id}-${subCategory.subcategoryId}`;
      if (subCategory.reversalCycleId) {
        id += `-${subCategory.reversalCycleId}`;
      }
    }

    return id;
  });

  unitPerformanceRows = derivedAsync<StudyUnitPerformanceRow[]>(() => {
    const study = this.study();
    const studySubcategories = Object.values(study.subcategories);
    const currentSubcategoryId = this.subCategoryId();
    const projectId = this.routeFacade.projectId;

    const outputRequest: OutputItemRequest[] = studySubcategories
      .filter(subcategory => subcategory.uuid === currentSubcategoryId || isNil(currentSubcategoryId))
      .map(subcategory => ({
        categoryCode: subcategory.categoryCode,
        segmentId: subcategory.segmentId,
        productTypeId: subcategory.productTypeId,
      }));

    return this.outputService.getOutputsConfiguration(projectId, outputRequest).pipe(
      tap(() => this.columnsReady.set(false)),
      withLatestFrom(this.#studyWorkingConditionStore.colors$),
      map(([outputs, colors]) => {
        return this.getNormalizedUnitPerformanceRows({
          subcategories: studySubcategories,
          subcategoryId: currentSubcategoryId,
          workingConditionColors: colors,
          outputs,
        });
      }),
      tap(rows => {
        this.unitWithAccessories = rows.filter(row => row.performance.accessories.length > 0);
        if (!isNil(currentSubcategoryId)) {
          this.showEnvelopesDialog(rows);
        }
      })
    );
  });

  readonly sectionTitle = computed<string>(() => {
    const title = this.resourcesService.getTextResource(445125);

    const rows = this.unitPerformanceRows();

    if (!rows) return title;

    return `${title} (${rows.length})`;
  });

  readonly hasInvalidUnits = computed(() => {
    const rows = this.unitPerformanceRows();
    if (!rows || rows.length === 0) return true;

    return rows.every(row => row.isAddDisabled);
  });

  private subs: SubSink = new SubSink();

  constructor() {
    super();

    explicitEffect([this.#selectedPrice], () => {
      this.tableRef()?.gridApi?.refreshCells({ columns: this.priceColumnsToRefresh });
    });

    // explicitEffect([computed(() => this.study().currencyCode)], () => {
    //   this.tableRef()?.gridApi?.refreshCells({ columns: this.priceColumnsToRefresh });
    // });

    if (!this.canDebug) {
      this.defaultColumnDefs.splice(3, 1);
      this.defaultColumnDefs.splice(this.defaultColumnDefs.length - 2, 2);
    }
  }

  get studyIsAhri() {
    const isActiveStudyCertified = this.studyFacade.isActiveStudyCertified;
    if (!isActiveStudyCertified) {
      return false;
    }

    const project = this.projectFacade.currentProjectDetails;
    return Object.values(project?.certification ?? {}).some(cert => cert.certificationId === 2) ?? false;
  }

  get studyIsChina() {
    const isActiveStudyCertified = this.studyFacade.isActiveStudyCertified;
    if (!isActiveStudyCertified) {
      return false;
    }
    const project = this.projectFacade.currentProjectDetails;
    return Object.values(project?.certification ?? {}).some(cert => cert.certificationId === 3) ?? false;
  }

  ngOnInit() {
    this.subs.sink = this.userSettingsFacade.showPrices$.subscribe(showPrices => {
      this.showPrices = showPrices;
      this.tableRef()?.gridApi.refreshCells({ columns: this.priceColumnsToRefresh });
    });
    this.tempStudyItems = this.#temporaryStudyItemsService.getItems();
    this.defaultColumnDefs = [
      ...this.getInitialDefaultColumnDefs(this.isInterimStudy()),
      {
        headerName: this.resourcesService.getTextResource('445156'),
        headerTooltip: this.resourcesService.getTextResource('445156'),
        field: 'unitModelId',
        cellClassRules: this.rowSpanClassRule,
        cellClass: this.isExperimental,
        rowSpan: this.rowSpan,
        pinned: 'left',
        type: this.defaultModelColumnTypes,
        hide: !this.hasDatasheetMoreDetails,
      },
      {
        headerName: this.resourcesService.getTextResource('445157'),
        headerTooltip: this.resourcesService.getTextResource('445157'),
        field: 'unitModelName',
        cellClassRules: this.rowSpanClassRule,
        pinned: 'left',
        rowSpan: this.rowSpan,
        cellClass: this.isExperimental,
        type: this.defaultModelColumnTypes,
      },
      {
        headerName: this.resourcesService.getTextResource('445158'),
        headerTooltip: this.resourcesService.getTextResource('445158'),
        field: 'unitSegment',
        valueFormatter: GridUtils.getNullishValueFormatter,
        rowSpan: this.rowSpan,
        filter: 'agSetColumnFilter',
        cellClassRules: this.rowSpanClassRule,
        type: this.defaultModelColumnTypes,
        hide: true,
      },
      {
        headerName: this.resourcesService.getTextResource('445159'),
        headerTooltip: this.resourcesService.getTextResource('445159'),
        field: 'unitBrand',
        filter: 'agSetColumnFilter',
        valueFormatter: GridUtils.getNullishValueFormatter,
        rowSpan: this.rowSpan,
        cellClassRules: this.rowSpanClassRule,
        type: this.defaultModelColumnTypes,
        hide: true,
      },
      {
        headerName: this.resourcesService.getTextResource('445160'),
        headerTooltip: this.resourcesService.getTextResource('445160'),
        field: 'unitClass',
        filter: 'agSetColumnFilter',
        minWidth: 200,
        rowSpan: this.rowSpan,
        cellClassRules: this.rowSpanClassRule,
        type: this.defaultModelColumnTypes,
        valueFormatter: GridUtils.getNullishValueFormatter,
        hide: true,
      },
      {
        headerName: this.resourcesService.getTextResource('445161'),
        headerTooltip: this.resourcesService.getTextResource('445161'),
        field: 'unitSerieRevId',
        rowSpan: this.rowSpan,
        cellClassRules: this.rowSpanClassRule,
        type: this.defaultModelColumnTypes,
        hide: true,
      },
      {
        headerComponent: TablePriceHeaderComponent,
        field: 'unitTotalPrices',
        rowSpan: this.rowSpan,
        filter: 'agNumberColumnFilter',
        cellClassRules: { ...this.rowSpanClassRule, ...this.priceClassRules },
        type: this.defaultModelColumnTypes,
        minWidth: 150,
        valueGetter: params => {
          const _selectedPrice = this.#selectedPrice();
          if (_selectedPrice) {
            const data = params.data as StudyUnitPerformanceRow;
            return data.unitTotalPrices[_selectedPrice];
          }
          return null;
        },
        valueFormatter: this.unitPriceValueFormatter.bind(this),
      },
      {
        headerName: this.resourcesService.getTextResource('445163'),
        headerTooltip: this.resourcesService.getTextResource('445163'),
        field: 'unitAccessoriesCount',
        rowSpan: this.rowSpan,
        filter: 'agNumberColumnFilter',
        type: this.defaultModelColumnTypes,
        cellClassRules: this.rowSpanClassRule,
        cellRenderer: TableClickableCellComponent,
        cellRendererParams: {
          values: {
            action: tableIconsActions.SHOW_ACCESSORIES_WITH_PRICE,
          },
        },
      },

      ...this.getFinalDefaultColumnDefs(this.isInterimStudy()),
    ];

    this.columnDefs = [...this.defaultColumnDefs];
  }

  rowSpan(params) {
    return params.data.rowSpan;
  }

  isExperimental(params) {
    const rowData = params.data as StudyUnitPerformanceRow;

    return rowData.unitIsPublicInElcatech === false ? 'bg-experimental' : '';
  }

  toggleStudy() {
    this.userSettingsFacade.toggleWorkingCondition();
  }

  getRowStyle(params: RowClassParams) {
    return params.data.workingConditionColor ? { background: params.data.workingConditionColor } : undefined;
  }

  setSelectedRow(evt: SelectionChangedEvent) {
    this.selectedRows = evt.api.getSelectedRows();
  }

  goToModelSearch() {
    const project = this.projectFacade.currentProject;
    const study = this.studyFacade.activeStudy;
    let classes$: Observable<Class[]>;

    if (study?.certified) {
      classes$ = this.sharedDataFacade.getClassesWithPerformanceForCertifiedStudy(
        Object.values(project?.details?.certification ?? {})
      );
    } else {
      classes$ = this.sharedDataFacade.classWithPerformance$;
    }

    if (this.hasChillersFreeStManagePermission) {
      classes$ = classes$.pipe(map(classes => [...classes, CHILLER_FREE_ST_CLASS]));
    }

    const dialogRef = this.dialogService.openDialog(ClassSelectionComponent, '75%', {
      class$: classes$,
    });
    dialogRef.afterClosed().subscribe((result: Nullable<Class>) => {
      if (!result) {
        return;
      }

      if (this.hasChillersFreeStManagePermission && result.name === CHILLER_FREE_ST_CLASS_NAME) {
        this.router.navigate(['chiller-free-st'], { relativeTo: this.activatedRoute });
        return;
      }

      if (result.id) {
        this.router.navigate(['search', 'class', result.id], { relativeTo: this.activatedRoute });
      }
    });
  }

  deleteUnits() {
    const projectId = this.projectFacade.currentProjectId;
    const unitsId = this.selectedRows.map(unit => unit.unitUuid).join(',');
    this.subs.sink = this.dialogService
      .openConfirmationDialog({
        titleResource: {
          resourceId: this.selectedRows.length > 1 ? 446070 : 445190,
        },
      })
      .subscribe(response => {
        if (response) {
          this.studyService.deleteUnits(projectId, unitsId).subscribe();
        }
      });
  }

  openPerformanceAssignmentDialog() {
    const data: PerformanceAccessoriesAssignmentDialogProps = {
      models: this.unitWithAccessories.map(item => ({
        unitUUID: item.unitUuid,
        modelName: item.unitModelName,
        performanceId: item.performanceId,
        performanceAccessories: item.performance.accessories,
        workingCondition: item.workingConditionName,
      })),
      callBackFunction: this.assignPerformanceAccessories.bind(this),
      allowClone: true,
    };

    this.dialogService.openDialog(PerformanceAccessoriesAssignmentDialogComponent, 'lg', data);
  }

  assignPerformanceAccessories(
    models: PerformanceAccessoriesAssignmentDialogResult['models'],
    action: PerformanceAccessoriesAssignmentDialogAction
  ) {
    const temporaryStudyId = this.routeFacade.interimStudyId || '';

    const unitAnswers = Object.entries(models).reduce<UnitPerformanceAccessoryAnswers[]>((acc, [key, value]) => {
      acc.push({
        unitId: key,
        performanceId: value.performanceId || '',
        accessories: value.accessories,
      });
      return acc;
    }, []);

    switch (action) {
      case PerformanceAccessoriesAssignmentDialogAction.Apply:
        this.traceRequest((requestTraceId, requestFailed) => {
          this.studyFacade
            .assignMultiplePerformanceAccessories(unitAnswers, temporaryStudyId, requestTraceId)
            .subscribe(() => {
              this.performanceAccessoriesAssigned.emit();
              requestFailed();
            });
        });
        break;
      case PerformanceAccessoriesAssignmentDialogAction.ApplyAndClone:
        this.traceRequest((requestTraceId, requestFailed) => {
          this.studyFacade
            .assignUnitAndAssignMultiplePerformanceAccessories(unitAnswers, temporaryStudyId, requestTraceId)
            .subscribe(() => {
              this.performanceAccessoriesAssigned.emit();
              requestFailed();
            });
        });
        break;
    }
  }

  onAction(event: TableActionClickEvent) {
    const rowData = event.data as StudyUnitPerformanceRow;
    const unitUuid = rowData.unitUuid;

    switch (event.action) {
      case TableIconActions.Delete:
        this.subs.sink = this.dialogService
          .openConfirmationDialog({
            titleResource: {
              resourceId: 445190,
            },
          })
          .subscribe(response => {
            if (response) {
              this.studyFacade.deleteUnit(rowData.subcategoryId, unitUuid);
            }
          });
        break;
      case tableIconsActions.TOGGLE_CREATE_SINGLE_UNIT: {
        const projectId = this.routeFacade.projectId || '';
        const temporaryStudyId = this.routeFacade.interimStudyId || '';
        const parentWorkingConditionId = this.routeFacade.queryParams['parentWorkingConditionId'] as string | undefined;

        this.traceRequest((requestTraceId, requestFailed) => {
          this.studyService
            .createSingleUnit(
              projectId,
              unitUuid,
              temporaryStudyId,
              rowData.workingConditionId,
              !this.isFromStudy() ? rowData.performanceId : undefined,
              parentWorkingConditionId,
              requestTraceId
            )
            .subscribe({
              next: () => {
                this.requestTraceData[requestTraceId] = {
                  unitToHighlight: `${unitUuid}_${rowData.performanceId}`,
                  toastRequestId: 445180,
                };
              },
              error: () => {
                requestFailed();
              },
            });
        });
        break;
      }
      case tableIconsActions.TOGGLE_GOOD_FOR_SELECTION:
        this.studyFacade.toggleGoodForSelection(unitUuid, rowData.subcategoryId, rowData.performanceId);
        break;
      case tableIconsActions.SHOW_WORKING_CONDITIONS: {
        const body: ConfigurationAlertsDialogComponentProps = {
          messages: event.data.messages,
          workingCondition: event.data.title,

          unitName: event.data.unitName,
          externalUnitName: event.data.externalUnitName,

          unitConfigErrorCode: event.data.unitConfigErrorCode,
          externalUnitConfigErrorCode: event.data.externalUnitConfigErrorCode,

          accessoriesErrorConfigCodes: event.data.accessoriesErrorConfigCodes,
        };
        this.dialogService.openDialog(ConfigurationAlertsDialogComponent, 'md', body);
        break;
      }
      case tableIconsActions.GOTO_UNIT_CONFIGURATION:
        if (isNil(unitUuid)) {
          return;
        }
        this.editUnitConfiguration(rowData);
        break;
      case tableIconsActions.DOWNLOAD_PERFORMANCE_REQUEST_LOG:
        this.studyFacade.downloadPerformanceRequestLog(rowData.workingConditionId, unitUuid);
        break;
      case tableIconsActions.ASSIGN_PERFORMANCE_ACCESSORIES: {
        const assignPerfFn = (
          models: PerformanceAccessoriesAssignmentDialogResult['models'],
          action: PerformanceAccessoriesAssignmentDialogAction
        ) => {
          const firstModel = Object.values(models)[0];
          switch (action) {
            case PerformanceAccessoriesAssignmentDialogAction.Apply:
              this.studyFacade.assignPerformanceAccessories(
                rowData.performanceId,
                unitUuid,
                this.isInterimStudy(),
                firstModel.accessories
              );
              break;
            case PerformanceAccessoriesAssignmentDialogAction.ApplyAndClone:
              this.traceRequest((requestTraceId, requestFailed) => {
                this.studyFacade
                  .assignUnitAndAssignPerformanceAccessories(
                    rowData.performanceId,
                    unitUuid,
                    this.isInterimStudy(),
                    firstModel.accessories,
                    requestTraceId
                  )
                  .subscribe(() => {
                    requestFailed();
                  });
              });
              break;
          }
        };

        const data: PerformanceAccessoriesAssignmentDialogProps = {
          models: [
            {
              unitUUID: rowData.unitUuid,
              modelName: rowData.unitModelName,
              performanceId: rowData.performanceId,
              performanceAccessories: rowData.performance.accessories,
              workingCondition: rowData.workingConditionName,
            },
          ],
          callBackFunction: assignPerfFn,
          allowClone: true,
        };

        this.dialogService.openDialog(PerformanceAccessoriesAssignmentDialogComponent, 'lg', data);
        break;
      }
      case tableIconsActions.SHOW_ACCESSORIES_WITH_PRICE:
        this.dialogService.openDialog(UnitAccessoryListDialogComponent, 'md', {
          unitModelName: rowData.unitModelName,
          accessories: rowData.unitAdditionalAccessories,
        } as UnitAccessoriesListDialogData);
        break;
      case tableIconsActions.SHOW_EXTERNAL_UNIT_ACCESSORIES_WITH_PRICE:
        this.dialogService.openDialog(UnitAccessoryListDialogComponent, 'md', {
          unitModelName: rowData.externalUnitModelName,
          accessories: rowData.externalUnitAdditionalAccessories,
        } as UnitAccessoriesListDialogData);
        break;
      case tableIconsActions.SHOW_ENVELOPES:
        this.spinnerService.showMainSpinner();
        this.studyService
          .showEnvelopes(this.routeFacade.projectId, this.study().uuid, rowData.workingConditionId, rowData.unitUuid)
          .pipe(finalize(() => this.spinnerService.hideMainSpinner()))
          .subscribe(envelopes => {
            this.spinnerService.hideMainSpinner();
            const images: DialogImage[] = envelopes.map(envelope => ({
              title: envelope.titleDescription,
              base64: 'data:image/png;base64,' + envelope.diagramBase64,
              description: envelope.functionDescriptionId,
            }));

            if (images.length > 0) {
              const data: ImageDialogData = {
                title: '445145',
                images: images,
              };
              this.imageDialogComponentRef = this.dialogService.openDialogScroll(ImageDialogComponent, 'xl', data);
            }
          });
        break;
    }
  }

  onFilterChanged(event: FilterChangedEvent) {
    this.updateSubcategoryFilteredUnitCount(event.api);
  }

  onRowDataUpdated(event: RowDataUpdatedEvent) {
    this.updateSubcategoryFilteredUnitCount(event.api);
  }

  onFiltersReset() {
    if (this.isInterimStudy()) {
      return;
    }

    const subCategoryId = this.subCategoryId();
    if (!subCategoryId) {
      return;
    }

    this.#performanceFiltersStore.setSubcategoryAppliedPerformanceFilters(subCategoryId, []);
    this.updatePerformanceHeaderFilter();
  }

  private updateSubcategoryFilteredUnitCount(gridApi: GridApi) {
    const subCategoryId = this.subCategoryId();
    if (!subCategoryId) {
      return;
    }
    const uniqueUnits = new Set<string>();
    gridApi.forEachNodeAfterFilterAndSort((node: IRowNode) => {
      uniqueUnits.add((node.data as StudyUnitPerformanceRow).unitUuid);
    });

    this.#performanceFiltersStore.setSubcategoryFilteredUnitCount(subCategoryId, uniqueUnits.size);
  }

  private editUnitConfiguration(rowData: StudyUnitPerformanceRow) {
    if (rowData.hasPerformanceErrorMessages) {
      this.toastService.showToast({ resourceId: '451549' });
      return;
    }

    this.studyFacade.editUnitConfiguration(
      this.study().uuid,
      rowData.unitUuid,
      rowData.performanceId,
      rowData.workingConditionId,
      this.routeFacade.isStudyModule
    );
  }

  private createDynamicColumns(data: {
    totalRows: number;
    uniquePerformanceValues: PerformanceValue[];
    shouldAddPerformanceMessagesColumn: boolean;
    shouldAddErpCompliantColumn: boolean;
    shouldAddExternalUnitColumns: boolean;
    shouldAddEnvelopesColumn: boolean;
  }) {
    const {
      totalRows,
      uniquePerformanceValues,
      shouldAddPerformanceMessagesColumn,
      shouldAddErpCompliantColumn,
      shouldAddExternalUnitColumns,
      shouldAddEnvelopesColumn,
    } = data;
    const collSortable = totalRows < 2;

    this.performanceColumnsCreated.emit(uniquePerformanceValues);
    this.columnDefs = [...this.defaultColumnDefs];

    if (shouldAddExternalUnitColumns) {
      this.columnDefs.splice(
        6,
        0,
        {
          headerName: this.resourcesService.getTextResource('451570'),
          headerTooltip: this.resourcesService.getTextResource('451570'),
          field: 'externalUnitModelId',
          valueFormatter: GridUtils.getNullishValueFormatter,
          cellClass: this.isExperimental,
          pinned: 'left',
          type: this.defaultExternalModelColumnTypes,
          hide: !this.hasDatasheetMoreDetails,
        },
        {
          headerName: this.resourcesService.getTextResource('451571'),
          headerTooltip: this.resourcesService.getTextResource('451571'),
          field: 'externalUnitModelName',
          valueFormatter: GridUtils.getNullishValueFormatter,
          pinned: 'left',
          cellClass: this.isExperimental,
          type: this.defaultExternalModelColumnTypes,
        }
      );

      this.columnDefs.splice(
        14,
        0,
        {
          headerName: this.resourcesService.getTextResource('451572'),
          headerTooltip: this.resourcesService.getTextResource('451572'),
          field: 'externalUnitSegment',
          valueFormatter: GridUtils.getNullishValueFormatter,
          filter: 'agSetColumnFilter',
          hide: true,
        },
        {
          headerName: this.resourcesService.getTextResource('451573'),
          headerTooltip: this.resourcesService.getTextResource('451573'),
          field: 'externalUnitBrand',
          valueFormatter: GridUtils.getNullishValueFormatter,
          filter: 'agSetColumnFilter',
          hide: true,
        },
        {
          headerName: this.resourcesService.getTextResource('451574'),
          headerTooltip: this.resourcesService.getTextResource('451574'),
          field: 'externalUnitClass',
          valueFormatter: GridUtils.getNullishValueFormatter,
          filter: 'agSetColumnFilter',
          minWidth: 200,
          hide: true,
        },
        {
          headerName: this.resourcesService.getTextResource('451575'),
          headerTooltip: this.resourcesService.getTextResource('451575'),
          field: 'externalUnitSerieRevId',
          valueFormatter: GridUtils.getNullishValueFormatter,
          hide: true,
        },
        {
          cellClassRules: this.priceClassRules,
          field: 'externalUnitTotalPrices',
          filter: 'agNumberColumnFilter',
          headerComponent: TablePriceHeaderComponent,
          headerComponentParams: {
            resourceId: '451576',
          },
          minWidth: 150,
          valueFormatter: this.externalUnitPriceValueFormatter.bind(this),
          valueGetter: params => {
            const _selectedPrice = this.#selectedPrice();
            if (_selectedPrice) {
              const data = params.data as StudyUnitPerformanceRow;
              return data.externalUnitTotalPrices[_selectedPrice];
            }
            return null;
          },
        },
        {
          headerName: this.resourcesService.getTextResource('445163'),
          headerTooltip: this.resourcesService.getTextResource('445163'),
          field: 'externalUnitAccessoriesCount',
          filter: 'agNumberColumnFilter',
          cellRenderer: TableClickableCellComponent,
          cellRendererParams: {
            values: {
              action: tableIconsActions.SHOW_EXTERNAL_UNIT_ACCESSORIES_WITH_PRICE,
            },
          },
        }
      );
    }

    for (const perf of uniquePerformanceValues) {
      const fieldId = generatePerformanceKey(perf);
      const child: ColDef = {
        field: fieldId,
        headerName: perf.unitOfMeasurement ?? ' - ',
        headerTooltip: perf.unitOfMeasurement ?? ' - ',
        filter: perf.dataType === 1 ? 'agNumberColumnFilter' : 'agTextColumnFilter',
        hide: perf.isVisibleByDefault === false,
        headerComponent: this.isInterimStudy() ? null : StudyPerformanceHeaderComponent,
        valueGetter: params => this.resourcesService.getDataTypeTranslation(perf.dataType, params.data[fieldId]),
        headerComponentParams: {
          fieldId,
          subcategoryId: this.subCategoryId(),
          dataType: perf.dataType,
          updatePerformanceHeaderFilter: this.updatePerformanceHeaderFilter.bind(this),
        },
        comparator: this.getPerformanceDataColumnComparator(perf),
      };

      const translatedHeader = this.resourcesService.getTextResourceWithPrefixSuffix({
        resourceId: perf.resourceId,
        prefixResourceId: perf.prefixResourceId,
        suffixResourceId: perf.suffixResourceId,
      });
      const columnHeader = {
        field: fieldId,
        headerName: translatedHeader,
        headerGroupComponent: TableResourceHeaderComponent,
        headerGroupComponentParams: {
          prefixResourceId: perf.prefixResourceId,
          resourceId: perf.resourceId,
          suffixResourceId: perf.suffixResourceId,
          iconResourceId: perf.iconResourceId,
          tooltipResourceId: perf.extendedResourceId,
          debugTooltip: perf.id,
        } as TableResourceHeaderComponentParams,
        sortable: collSortable,
        children: [child],
      } as ColDef;

      this.columnDefs.push(columnHeader);
    }

    if (shouldAddPerformanceMessagesColumn) {
      this.addPerformanceMessagesColumn();
    }
    if (shouldAddEnvelopesColumn) {
      this.addEnvelopesColumn();
    }
    if (shouldAddErpCompliantColumn) {
      this.addErpCompliantColumn();
    }
    this.addAhriCertificationColumn();
    this.addChinaCertificationColumn();

    this.columnsReady.set(true);
  }

  formatPrice(prices: StudyUnitTotalPrices, hasAccessories: boolean): string {
    const _selectedPrice = this.#selectedPrice();
    if (!_selectedPrice || !prices[_selectedPrice]) {
      return '-';
    }

    // APPLYME-6060: originalPrice must render in the unit's source currency (originalCurrencyCode),
    // not in the study currency.
    const currencyCode =
      _selectedPrice === PriceTypeEnum.originalPrice && prices.originalCurrencyCode
        ? prices.originalCurrencyCode
        : (this.overrideCurrencyCode() ?? this.study().currencyCode);

    const formattedPrice = this.projectCurrencyPipe.transform(prices[_selectedPrice], currencyCode);
    return hasAccessories ? `${formattedPrice}*` : formattedPrice;
  }

  private unitPriceValueFormatter(params: ValueFormatterParams): string {
    if (!this.showPrices) return '-';

    const data = params.data as StudyUnitPerformanceRow;
    return this.formatPrice(data.unitTotalPrices, data.unitAccessoriesCount > 0);
  }

  private externalUnitPriceValueFormatter(params: ValueFormatterParams): string {
    if (!this.showPrices) return '-';

    const data = params.data as StudyUnitPerformanceRow;
    return this.formatPrice(
      data.externalUnitTotalPrices,
      !isNil(data.externalUnitAccessoriesCount) && data.externalUnitAccessoriesCount > 0
    );
  }

  private normalizePerformanceData(performance: PerformanceValue[]) {
    return performance.reduce(
      (acc, perf) => {
        acc[generatePerformanceKey(perf)] = perf.value;

        return acc;
      },
      {} as Record<string, string>
    );
  }

  showEnvelopesDialog(unitPerformanceRows: StudyUnitPerformanceRow[]) {
    if (this.imageDialogComponentRef) {
      this.imageDialogComponentRef.close();
    }

    const envelopes: PerformanceEnvelop[] = flatMap(unitPerformanceRows.map(item => item.performance.envelopes));
    const images: DialogImage[] = [];

    const performanceByWorkingCondition = groupBy(unitPerformanceRows, 'workingConditionId');
    Object.keys(performanceByWorkingCondition).forEach(key => {
      const performanceGrouped = performanceByWorkingCondition[key];
      const envelopesGrouped = flatMap(performanceGrouped.map(item => item.performance.envelopes));
      const performanceArrayGrouped = flatMap(performanceGrouped.map(item => item.performance.performanceValues));

      if (envelopesGrouped.length === 0 || performanceArrayGrouped.length !== 0) {
        return;
      }

      envelopes.forEach(envelope => {
        if (!envelope.outOfLimit) {
          return;
        }
        if (images.some(image => image.base64?.includes(envelope.diagramBase64))) {
          return; // skip duplicates
        }
        const image = {
          title: envelope.titleDescription,
          base64: 'data:image/png;base64,' + envelope.diagramBase64,
          description: envelope.functionDescriptionId,
        };
        images.push(image);
      });
    });

    if (images.length > 0) {
      const data: ImageDialogData = {
        title: '445145',
        images: images,
      };
      this.imageDialogComponentRef = this.dialogService.openDialogScroll(ImageDialogComponent, 'xl', data);
    }
  }

  exportTableCsv() {
    const subCategoryId = this.subCategoryId();
    if (!subCategoryId) {
      return;
    }

    this.spinnerService.showMainSpinner();

    this.subs.sink = this.studyService
      .exportSubcategoryCsv(this.routeFacade.projectId, this.studyFacade.activeStudyId, subCategoryId)
      .subscribe(response => {
        this.spinnerService.hideMainSpinner();
        const blob = new Blob([response.body as Blob], { type: 'application/octet-stream;charset=UTF-8' });
        saveAs(blob, MiscUtils.extractFileName(response.headers.get('Content-Disposition')));
      });
  }

  /**
   * AG Grid post-sort callback - handles column sorts while maintaining unit grouping.
   * Optimized for large datasets by minimizing allocations and iterations.
   *
   * Strategy:
   * 1. Group all rows by unitUuid
   * 2. Within each unit group, sort by: working condition (main first, then by timestamp)
   * 3. Sort unit groups based on the column sort (using the "representative" row)
   * 4. Recalculate rowSpans to reflect the new order
   */
  postSortRowsByUnits: (params: PostSortRowsParams) => void = (params: PostSortRowsParams) => {
    const nodes = params.nodes;
    if (!nodes || nodes.length === 0) return;

    // Store original sorted order (reflects user's column sort)
    const originalIndexMap = new Map<IRowNode, number>();
    for (let i = 0; i < nodes.length; i++) {
      originalIndexMap.set(nodes[i], i);
    }

    // Group nodes by unitUuid and sort within groups
    const nodesByUnit = groupBy(nodes, node => (node.data as StudyUnitPerformanceRow).unitUuid);
    const sortedNodesByUnit = new Map<string, IRowNode[]>();
    const mainRowIndices = new Map<string, number>(); // Store index instead of node for efficiency

    // Process each unit group
    Object.entries(nodesByUnit).forEach(([unitUuid, unitNodes]) => {
      // Detect if this unit has multiple performances per working condition
      const wcPerformanceCount = new Map<string, number>();
      for (const node of unitNodes) {
        const wcId = (node.data as StudyUnitPerformanceRow).workingConditionId;
        wcPerformanceCount.set(wcId, (wcPerformanceCount.get(wcId) || 0) + 1);
      }
      const hasMultiplePerformancesPerWC = Array.from(wcPerformanceCount.values()).some(count => count > 1);

      // Sort comparator: behavior changes based on whether we have multiple performances per WC
      const sortUnitNodes = (a: IRowNode, b: IRowNode): number => {
        const dataA = a.data as StudyUnitPerformanceRow;
        const dataB = b.data as StudyUnitPerformanceRow;

        if (hasMultiplePerformancesPerWC) {
          // Multiple performances per WC: Keep WCs grouped, sort performances within each WC

          // Primary: Group by working condition (main first, then by creation timestamp)
          if (dataA.isMainWorkingCondition !== dataB.isMainWorkingCondition) {
            return dataA.isMainWorkingCondition ? -1 : 1;
          }

          if (dataA.workingConditionCreatedAt !== dataB.workingConditionCreatedAt) {
            return dataA.workingConditionCreatedAt - dataB.workingConditionCreatedAt;
          }

          // Secondary: Within same working condition, sort by user's column sort
          if (dataA.workingConditionId === dataB.workingConditionId) {
            const indexA = originalIndexMap.get(a) ?? 0;
            const indexB = originalIndexMap.get(b) ?? 0;
            return indexA - indexB;
          }

          // Fallback: Performance index
          return dataB.performanceIndex - dataA.performanceIndex;
        } else {
          // Single performance per WC: Allow WCs to be reordered by column sort

          // Primary: Sort by user's column sort order
          const indexA = originalIndexMap.get(a) ?? 0;
          const indexB = originalIndexMap.get(b) ?? 0;

          if (indexA !== indexB) {
            return indexA - indexB;
          }

          // Tiebreaker 1: Main working condition first
          if (dataA.isMainWorkingCondition !== dataB.isMainWorkingCondition) {
            return dataA.isMainWorkingCondition ? -1 : 1;
          }

          // Tiebreaker 2: Working condition creation timestamp
          if (dataA.workingConditionCreatedAt !== dataB.workingConditionCreatedAt) {
            return dataA.workingConditionCreatedAt - dataB.workingConditionCreatedAt;
          }

          // Tiebreaker 3: Performance index
          return dataB.performanceIndex - dataA.performanceIndex;
        }
      };

      // Sort nodes within unit group
      const sortedUnitNodes = unitNodes.sort(sortUnitNodes);
      sortedNodesByUnit.set(unitUuid, sortedUnitNodes);

      // Find representative row (first main working condition row with lowest performance index)
      let mainRowIndex = -1;
      let lowestPerfIndex = Number.MAX_SAFE_INTEGER;

      for (const node of sortedUnitNodes) {
        const data = node.data as StudyUnitPerformanceRow;
        if (data.isMainWorkingCondition && data.performanceIndex < lowestPerfIndex) {
          lowestPerfIndex = data.performanceIndex;
          mainRowIndex = originalIndexMap.get(node) ?? -1;
        }
      }

      if (mainRowIndex !== -1) {
        mainRowIndices.set(unitUuid, mainRowIndex);
      }
    });

    // Sort unit groups based on their representative row's original position
    const sortedUnitUuids = Array.from(sortedNodesByUnit.keys()).sort((uuidA, uuidB) => {
      const indexA = mainRowIndices.get(uuidA) ?? Number.MAX_SAFE_INTEGER;
      const indexB = mainRowIndices.get(uuidB) ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });

    // Rebuild nodes array with sorted groups
    nodes.length = 0;
    for (const unitUuid of sortedUnitUuids) {
      const unitNodes = sortedNodesByUnit.get(unitUuid);
      if (unitNodes) {
        nodes.push(...unitNodes);
      }
    }

    // Recalculate row spans and redraw
    this.calculateRowSpansForNodes(nodes);
    params.api.redrawRows();
  };

  /**
   * RowSpan calculation after sorting.
   * Uses a clean two-pass approach: first identify group sizes, then apply rowspans.
   */
  private calculateRowSpansForNodes(nodes: IRowNode[]): void {
    if (!nodes || nodes.length === 0) return;

    // First pass: build map of unit group sizes
    const unitGroupSizes = new Map<string, number>();
    let currentUnitId = '';
    let groupSize = 0;

    for (const node of nodes) {
      const unitId = (node.data as StudyUnitPerformanceRow).unitUuid;

      if (unitId !== currentUnitId) {
        if (currentUnitId) {
          unitGroupSizes.set(currentUnitId, groupSize);
        }
        currentUnitId = unitId;
        groupSize = 1;
      } else {
        groupSize++;
      }
    }
    // Store the last group
    if (currentUnitId) {
      unitGroupSizes.set(currentUnitId, groupSize);
    }

    // Second pass: apply rowspans (first row gets full count, others get 1)
    currentUnitId = '';
    for (const node of nodes) {
      const rowData = node.data as StudyUnitPerformanceRow;
      const unitId = rowData.unitUuid;

      if (unitId !== currentUnitId) {
        currentUnitId = unitId;
        rowData.rowSpan = unitGroupSizes.get(unitId) || 1;
      } else {
        rowData.rowSpan = 1;
      }
    }
  }

  private getPerformanceDataColumnComparator(column: PerformanceValue) {
    return (valueA: any, valueB: any, nodeA: IRowNode, nodeB: IRowNode, isDescending: boolean): number => {
      if (valueA === valueB) {
        return 0;
      }

      // valueA and valueB is either integer or decimal
      if (column.dataType === 1) {
        const parsedA = isNil(valueA) || valueA === '-' ? 0 : parseFloat(valueA);
        const parsedB = isNil(valueB) || valueB === '-' ? 0 : parseFloat(valueB);

        return parsedA - parsedB;
      }

      // valueA and valueB is a string
      if (column.dataType === 2 || column.dataType === 3) {
        return valueA === valueB ? 0 : valueA > valueB ? 1 : -1;
      }

      return 0;
    };
  }

  private getInitialDefaultColumnDefs(isTemporaryStudy: boolean): ColDef[] {
    const selectColDef: ColDef = {
      headerName: '',
      rowSpan: this.rowSpan,
      cellClassRules: this.rowSpanClassRule,
      cellStyle: { 'background-color': '#FFFFFF' },
      pinned: 'left',
      resizable: false,
      width: 70,
      checkboxSelection: true,
    };
    const addColDef: ColDef = {
      ...TableGetCustomIconColumnDef({
        icon: 'a_add',
        secondaryIcon: 'a_add',
        toggleField: 'createSingleUnit',
        tooltip: '446089',
        isDisabled: (data: StudyUnitPerformanceRow) => data.isAddDisabled,
        isActive: (data: StudyUnitPerformanceRow) => {
          return this.tempStudyItems.includes(`${data.unitUuid}_${data.performanceId}`);
        },
        action: tableIconsActions.TOGGLE_CREATE_SINGLE_UNIT,
      }),
      headerName: 'Add',
      rowSpan: this.isFromStudy() ? this.rowSpan : undefined,
      cellClassRules: this.rowSpanClassRule,
      cellStyle: { 'background-color': '#FFFFFF' },
      pinned: 'left',
    };
    const editColDef: ColDef = {
      ...TableGetCustomIconColumnDef({
        icon: 'a_edit',
        action: tableIconsActions.GOTO_UNIT_CONFIGURATION,
        isDisabled: (data: StudyUnitPerformanceRow) => data.isGoodForSelection,
        tooltip: '446082',
      }),
      headerName: 'Edit',
      pinned: 'left',
    };
    const favoriteColDef: ColDef = {
      ...TableGetCustomIconColumnDef({
        icon: 'a_favorite',
        secondaryIcon: 'a_favorite',
        toggleField: 'goodForSelection',
        action: tableIconsActions.TOGGLE_GOOD_FOR_SELECTION,
        isActive: (data: StudyUnitPerformanceRow) => data.isGoodForSelection,
        isDisabled: (data: StudyUnitPerformanceRow) =>
          data.hasPerformanceErrorMessages ||
          !isNil(data.unitConfigErrorCode) ||
          !isNil(data.externalUnitConfigErrorCode) ||
          Object.values(data.accessoriesErrorConfigCodes).length > 0,
        tooltip: '446081',
      }),
      headerName: 'Add to selection',
      pinned: 'left',
    };
    const deleteColDef: ColDef = {
      ...TABLE_DEFAULT_DELETE_COL_DEF,
      headerName: 'Delete',
      rowSpan: this.rowSpan,
      cellClassRules: this.rowSpanClassRule,
      pinned: 'left',
      cellStyle: { 'background-color': '#FFFFFF' },
    };

    if (isTemporaryStudy) {
      return [addColDef, editColDef];
    }

    return [selectColDef, favoriteColDef, editColDef, deleteColDef];
  }

  private getFinalDefaultColumnDefs(isTemporaryStudy: boolean): ColDef[] {
    const alertColDef: ColDef = {
      ...TableGetCustomIconColumnDef({
        icon: 'aa_alert_filter',
        tooltip: '445155',
        action: tableIconsActions.ASSIGN_PERFORMANCE_ACCESSORIES,
        isHidden: (data: StudyUnitPerformanceRow) => data.performance.accessories.length === 0,
        isActive: () => true,
      }),
      headerName: 'Alert',
    };
    const downloadColDef: ColDef = {
      ...TableGetCustomIconColumnDef({
        icon: 'a_download',
        tooltip: '444783',
        action: tableIconsActions.DOWNLOAD_PERFORMANCE_REQUEST_LOG,
      }),
      headerName: 'Download',
    };
    const workingConditionTooltipColumn: ColDef = {
      ...TableGetCustomIconColumnDef({
        icon: 'a_thermometer',
        action: '',
        isButton: false,
        translateTooltip: false,
        tooltipClass: 'tooltip-multiline',
        tooltip: (data: StudyUnitPerformanceRow) => `${data.workingConditionName}\n${data.subcategoryName}`,
      }),
      headerName: 'Working condition',
    };

    const icons: ColDef[] = [];

    if (!isTemporaryStudy && this.canDebug) {
      icons.push(downloadColDef);
    }
    // DoR 019 C.3: only the All Models tab mixes subcategories, where the opacity scale alone is not enough
    if (isNil(this.subCategoryId())) {
      icons.push(workingConditionTooltipColumn);
    }
    icons.push(alertColDef);

    return icons;
  }

  private updatePerformanceHeaderFilter() {
    this.traceRequest((requestTraceId, requestFailed) => {
      const projectId = this.routeFacade.projectId;
      const studyId = this.routeFacade.studyId;

      const subCategoryId = this.subCategoryId();
      if (!subCategoryId) {
        return;
      }

      const performanceFilters: PerformanceFilter[] = Object.values(
        this.#performanceFiltersStore.getSubcategoryPerformanceFiltersSnapshot(subCategoryId)
      );
      const payload: PerformanceFiltersPayload = {
        derating: false,
        filterConditions: null,
        performanceFilters: performanceFilters,
      };
      this.studyService
        .updateFilterConditionsData(projectId, studyId, subCategoryId, payload, requestTraceId)
        .subscribe({
          error: requestFailed,
        });
    });
  }

  private addPerformanceMessagesColumn() {
    const errorColumn: ColDef = {
      headerName: this.resourcesService.getTextResource('445235'),
      headerTooltip: this.resourcesService.getTextResource('445235'),
      minWidth: 200,
      cellRenderer: TablePerformanceAlertsCellComponent,
      cellRendererParams: {
        values: {
          action: tableIconsActions.SHOW_WORKING_CONDITIONS,
        },
      },
    };
    this.columnDefs.splice(this.defaultColumnDefs.length, 0, errorColumn);
  }

  private addErpCompliantColumn() {
    const notErpCompliantColumn: ColDef = {
      field: 'erpCompliant',
      headerName: 'CE',
      headerTooltip: 'CE',
      cellRenderer: TableImageCellComponent,
      cellRendererParams: {
        values: {
          imageId: 75417,
          expectedValue: false,
        },
      },
    };
    this.columnDefs.splice(this.defaultColumnDefs.length, 0, notErpCompliantColumn);
  }

  private addAhriCertificationColumn() {
    if (!this.studyIsAhri) {
      return;
    }
    const ahriColumn: ColDef = {
      field: 'isAhri',
      headerName: this.resourcesService.getTextResource('444897'),
      headerTooltip: this.resourcesService.getTextResource('444897'),
      rowSpan: this.rowSpan,
      cellClassRules: this.rowSpanClassRule,
      cellStyle: { 'background-color': '#FFFFFF' },
      cellRenderer: TableImageCellComponent,
      cellRendererParams: {
        values: {
          imageId: 53967,
          expectedValue: true,
        },
      },
    };
    this.columnDefs.splice(this.defaultColumnDefs.length, 0, ahriColumn);
  }

  private addEnvelopesColumn() {
    const envelopesColumn: ColDef = {
      ...TableGetCustomIconColumnDef({
        faIcon: 'chart-line',
        tooltip: '445145',
        action: tableIconsActions.SHOW_ENVELOPES,
        isHidden: (data: StudyUnitPerformanceRow) => {
          return !data.performance.hasEnvelopes;
        },
      }),
      headerName: 'Envelopes',
    };
    this.columnDefs.splice(this.defaultColumnDefs.length, 0, envelopesColumn);
  }

  private addChinaCertificationColumn() {
    if (!this.studyIsChina) {
      return;
    }
    const chinaColumn: ColDef = {
      field: 'isChina',
      headerName: this.resourcesService.getTextResource('444897'),
      headerTooltip: this.resourcesService.getTextResource('444897'),
      rowSpan: this.rowSpan,
      cellClassRules: this.rowSpanClassRule,
      cellStyle: { 'background-color': '#FFFFFF' },
      cellRenderer: TableImageCellComponent,
      cellRendererParams: {
        values: {
          imageId: 3370,
          expectedValue: true,
        },
      },
    };
    this.columnDefs.splice(this.defaultColumnDefs.length, 0, chinaColumn);
  }

  private getUnitTotalPrices(
    unitAccessories: StudyUnitAccessoryWithPrice[],
    unit: StudyUnit | undefined
  ): StudyUnitTotalPrices {
    return {
      priceList: this.#unitUtils.getTotalAccessoriesPrice(unitAccessories, PriceTypeEnum.basePrice),
      sellIn: this.#unitUtils.getTotalAccessoriesPrice(unitAccessories, PriceTypeEnum.sellIn),
      local: this.#unitUtils.getTotalAccessoriesPrice(unitAccessories, PriceTypeEnum.local),
      // APPLYME-6060: aggregate source-currency total; originalCurrencyCode is a unit-level invariant.
      originalPrice: this.#unitUtils.getTotalAccessoriesPrice(unitAccessories, PriceTypeEnum.originalPrice),
      originalCurrencyCode: unit?.originalCurrencyCode,
    };
  }

  private getNormalizedUnitPerformanceRows(data: {
    subcategories: StudySubcategory[];
    subcategoryId: string | undefined;
    outputs: OutputConfiguration[];
    workingConditionColors: StudyWorkingConditionColorsMap;
  }): StudyUnitPerformanceRow[] {
    const { subcategories, subcategoryId, outputs, workingConditionColors } = data;
    const items = StudyUtils.getSubcategoriesUnitPerformanceItems({
      subcategories,
      subcategoryId,
      outputs,
    });
    const getUniquePerformances = flow(
      items => flatMap(items, item => item.performance.performanceValues),
      values => uniqBy(values, value => `${value.id}-${value.circuit}`)
    );
    const uniquePerformances = getUniquePerformances(items);
    const hasPerformanceMessages = items.find(item => item.performance.messages.length > 0) !== undefined;

    const hasConfigurationErrors =
      items.some(item => !isNil(item.unit.configErrorCode) || !isNil(item.externalUnit?.configErrorCode)) ||
      items.some(
        item =>
          Object.values(item.unit.accessories).some(accessory => !isNil(accessory.configErrorCode)) ||
          Object.values(item.externalUnit?.accessories ?? {}).some(accessory => !isNil(accessory.configErrorCode))
      );

    const shouldAddErpCompliantColumn =
      items.length > 0 &&
      items.some(data => data.performance.performanceValues.length > 0 && !data.performance.erpCompliant);

    this.createDynamicColumns({
      totalRows: items.length,
      uniquePerformanceValues: uniquePerformances,
      shouldAddPerformanceMessagesColumn: hasPerformanceMessages || hasConfigurationErrors,
      shouldAddErpCompliantColumn: shouldAddErpCompliantColumn,
      shouldAddExternalUnitColumns: items.some(item => item.externalUnit !== undefined),
      shouldAddEnvelopesColumn: items.some(item => item.performance.hasEnvelopes),
    });

    return items.map(item => {
      const {
        unit,
        externalUnit,
        isGoodForSelection,
        performanceId,
        performanceIndex,
        workingConditionCreatedAt,
        performance,
        subcategory,
        workingCondition,
        isMainWorkingCondition,
        prevWorkingConditionId,
      } = item;

      const unitAccessories = this.#unitUtils.getNonStandardAccessories(unit);
      const unitAdditionalAccessories = unitAccessories.filter(accessory => accessory.type !== AccessoryType.DASH);
      const externalUnitAccessories = externalUnit ? this.#unitUtils.getNonStandardAccessories(externalUnit) : [];
      const externalUnitAdditionalAccessories = externalUnitAccessories.filter(
        accessory => accessory.type !== AccessoryType.DASH
      );

      const subcategoryId = subcategory.uuid;
      const workingConditionId = workingCondition.uuid;

      const accessoriesErrorConfigCodes = Object.entries({
        ...unit.accessories,
        ...externalUnit?.accessories,
      }).reduce<AccessoriesErrorConfigCodes>((acc, [key, value]) => {
        if (value.configErrorCode) {
          acc[key] = {
            code: value.code,
            configErrorCode: value.configErrorCode,
            resourceId: value.resourceId,
          };
        }
        return acc;
      }, {});

      const unitTotalPrices = this.getUnitTotalPrices(unitAccessories, unit);
      const hasPerformanceErrorMessages = this.#unitUtils.hasPerformanceErrorMessages(performance.messages);
      const hasPrice = unitTotalPrices.local !== 0 || unitTotalPrices.sellIn !== 0;
      const isAddDisabled =
        (!this.routeFacade.isStudyModule && !hasPrice) || (this.isInterimStudy() && hasPerformanceErrorMessages);

      return {
        rowSpan: 1, // Will be recalculated based on consecutive unitUuid groupings
        isGoodForSelection,
        subcategoryId,
        subcategoryName: this.resourcesService.getTextResource(subcategory.resourceId),
        unitUuid: unit.uuid,
        unitModelSize: unit.modelSize,
        unitModelId: unit.modelId,
        unitName: unit.name,
        unitModelName: unit.modelName,
        unitSerieRevId: unit.serieRevId,
        unitIsPublicInElcatech: unit.isPublicInElcatech,
        unitClass: this.sharedDataFacade.getClassById(unit.classId)?.description,
        unitBrand: this.sharedDataFacade.getBrandById(unit.brandId)?.description,
        unitSegment: this.sharedDataFacade.getSegmentById(unit.segmentId)?.description,
        unitAdditionalAccessories,
        unitAccessoriesCount: unitAdditionalAccessories.length,
        unitTotalPrices,
        externalUnitModelId: externalUnit?.modelId,
        externalUnitName: externalUnit?.name,
        externalUnitModelName: externalUnit?.modelName,
        externalUnitSerieRevId: externalUnit?.serieRevId,
        externalUnitClass: externalUnit ? this.sharedDataFacade.getClassById(externalUnit.classId)?.description : '-',
        externalUnitBrand: externalUnit ? this.sharedDataFacade.getBrandById(externalUnit.brandId)?.description : '-',
        externalUnitSegment: externalUnit
          ? this.sharedDataFacade.getSegmentById(externalUnit.segmentId)?.description
          : '-',
        externalUnitAdditionalAccessories,
        externalUnitAccessoriesCount: externalUnit ? externalUnitAdditionalAccessories.length : undefined,
        externalUnitTotalPrices: this.getUnitTotalPrices(externalUnitAccessories, externalUnit),
        workingConditionName: workingCondition.name,
        workingConditionId,
        isMainWorkingCondition,
        prevWorkingConditionId,
        workingConditionColor: workingConditionColors[subcategoryId]
          ? workingConditionColors[subcategoryId][workingConditionId]
          : undefined,
        hasPerformanceErrorMessages,
        isAddDisabled,
        erpCompliant: performance.erpCompliant,
        performanceId,
        performanceIndex,
        workingConditionCreatedAt,
        performance,
        unitConfigErrorCode: unit.configErrorCode,
        externalUnitConfigErrorCode: externalUnit?.configErrorCode ?? null,
        accessoriesErrorConfigCodes,
        ...this.normalizePerformanceData(performance.performanceValues),
      };
    });
  }

  addAllUnits() {
    const body: ConfirmationDialogData = {
      titleResource: {
        resourceId: 446069,
        resourceParams: [this.unitPerformanceRows()?.length ?? 0],
      },
    };
    this.dialogService.openConfirmationDialog(body).subscribe(confirmed => {
      if (!confirmed) return;

      const projectId = this.routeFacade.projectId;
      const interimStudyId = this.routeFacade.interimStudyId;
      const parentWorkingConditionId = this.routeFacade.queryParams['parentWorkingConditionId'] as string | undefined;

      this.traceRequest((requestTraceId, requestFailed) => {
        this.studyService
          .addAllUnitsToStudy(projectId, interimStudyId, parentWorkingConditionId, requestTraceId)
          .subscribe({
            next: () => {
              this.requestTraceData[requestTraceId] = {
                toastRequestId: 445188,
                highlightAllRows: true,
              };
            },
            error: () => {
              requestFailed();
            },
          });
      });
    });
  }

  protected handleRequestTraceMessage(message: ParsedWebsocketMessage, requestTraceId: string) {
    super.handleRequestTraceMessage(message, requestTraceId);

    if (message.description === RequestTraceCommonMessages.RequestCompletedMessage) {
      const requestData = this.requestTraceData[requestTraceId] as RequestTraceData | undefined;
      if (!requestData) {
        return;
      }

      const toastRequestId = requestData.toastRequestId;

      if (toastRequestId) {
        this.toastService.showToast({ resourceId: toastRequestId });
      }
      if (requestData.highlightAllRows) {
        const unitUuids = this.unitPerformanceRows()?.map(row => `${row.unitUuid}_${row.performanceId}`) ?? [];
        this.#temporaryStudyItemsService.addItems(unitUuids);
        this.tempStudyItems = unitUuids;
      }
      if (requestData.unitToHighlight) {
        this.#temporaryStudyItemsService.addItem(requestData.unitToHighlight);
        this.tempStudyItems = this.#temporaryStudyItemsService.getItems();
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.subs.unsubscribe();
  }
}
